import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, ConfirmModal, Icon } from '../../../components';
import { Modal } from '../../../components/Modal';
import { ManagementPagination } from '../../../components/ManagementFilters';
import { pageCardClasses } from '../../../components/AppLayout/styles';
import { strings, sendAllMessageStrings, commonStrings } from '../../../constants';
import { getReservations, cancelReservation } from '../../../api/messages';
import { getAutoReplyMessageAssets } from '../../../api';
import { ScheduledReservation } from '../../../types';
import useToast from '../../../hooks/useToast';
import { useTags } from '../../../hooks/useTags';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { routes } from '../../../constants/routes';
import { useOrgPath } from '../../../hooks/useOrgPath';
import { getHttpStatus } from '../../../utils';
import { canFetchOrganizationFeature } from '../../../utils/organizationFeature';
import { EOrganizationFeature } from '../../../types/enum';
import MessagePreview from '../MessagePreview';
import { convertDraftSectionsToMessageSections } from '../utils';
import { ReservationsTable } from './ReservationsTable';
import { ReservationsCardList } from './ReservationsCardList';
import { queryKeys } from '../../../constants/queryKeys';

const PAGE_SIZE = 10;

function ReservationsList(): JSX.Element {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const { toastError, toastSuccess } = useToast();
  const { data: currentUser } = useCurrentUser();
  // タグ管理がOFFの組織ではタグAPIが403になるので取得しない（タグ指定の予約はID表記になる）
  const { tags } = useTags({
    enabled: canFetchOrganizationFeature(
      currentUser,
      EOrganizationFeature.TAG_MANAGEMENT,
    ),
  });
  const [reservations, setReservations] = useState<ScheduledReservation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [previewReservationId, setPreviewReservationId] = useState<
    string | null
  >(null);
  const { data: assets = [] } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
    queryFn: () => getAutoReplyMessageAssets(),
  });

  const countLabel = useMemo(
    () => `${total}${commonStrings.countSuffix}`,
    [total],
  );
  const loadReservations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getReservations({
        currentPage,
        pageSize: PAGE_SIZE,
      });
      setReservations(response.data);
      setTotal(response.total);
      setTotalPage(response.totalPage);
      setTruncated(response.truncated);
      // 取り消しで最終ページが消えたときに空ページへ取り残されないようにする
      if (response.totalPage > 0 && currentPage > response.totalPage) {
        setCurrentPage(response.totalPage);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  }, [toastError, currentPage]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const handleCancelReservation = useCallback(async () => {
    if (!cancelTargetId || isCanceling) return;
    setIsCanceling(true);
    try {
      await cancelReservation(cancelTargetId);
      toastSuccess(sendAllMessageStrings.reservations.messages.canceled);
      setPreviewReservationId((currentId) =>
        currentId === cancelTargetId ? null : currentId,
      );
      setCancelTargetId(null);
      // 取り消しで最終ページが消えることがあるため、先頭に戻さず再取得して追随する
      loadReservations();
    } catch (error) {
      // 配信が始まっていた場合は一覧の内容自体が古いので取り直す
      loadReservations();
      toastError(error);
      // 別タブで取り消された(404)・cronが配信を始めた(409)予約は確認モーダルに
      // 残しても操作できないため閉じる。ネットワーク断や5xxは再試行できるよう残す
      const status = getHttpStatus(error);
      if (status === 404 || status === 409) setCancelTargetId(null);
    } finally {
      setIsCanceling(false);
    }
  }, [
    cancelTargetId,
    isCanceling,
    toastError,
    toastSuccess,
    loadReservations,
  ]);

  // プレビュー内容は一覧から導出する。本文を別stateへ複製すると、取り消しや
  // 再取得で一覧が変わってもスナップショットが古いまま残る
  const previewReservation = useMemo(
    () =>
      reservations.find(
        (reservation) => reservation.id === previewReservationId,
      ) ?? null,
    [reservations, previewReservationId],
  );

  const closePreview = useCallback(() => {
    setPreviewReservationId(null);
  }, []);

  const handlePreviewReservation = useCallback(
    (reservation: ScheduledReservation) => {
      setPreviewReservationId((currentId) =>
        currentId === reservation.id ? null : reservation.id,
      );
    },
    [],
  );

  const handleEditReservation = useCallback(
    (reservationId: string) => {
      navigate(
        orgPathFor(`${routes.SEND_MESSAGE_FOR_ALL}?reservationId=${reservationId}`),
      );
    },
    [navigate, orgPathFor],
  );

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {sendAllMessageStrings.reservations.listTitle}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={currentPage}
              totalPage={totalPage}
              loading={isLoading}
              onPrevious={() =>
                setCurrentPage((prev) => Math.max(1, prev - 1))
              }
              onNext={() => setCurrentPage((prev) => prev + 1)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate(orgPathFor(routes.SEND_MESSAGE_FOR_ALL))}
            >
              {sendAllMessageStrings.backToSendAll}
            </Button>
          </div>
        </header>

        {truncated && (
          <p className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
            {sendAllMessageStrings.reservations.truncatedNotice}
          </p>
        )}

        <div className="relative">
          <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
            <ReservationsTable
              reservations={reservations}
              assets={assets}
              tags={tags}
              loading={isLoading}
              onEdit={handleEditReservation}
              onCancel={(reservationId) => setCancelTargetId(reservationId)}
              onPreview={handlePreviewReservation}
            />
          </div>
          <div className="mt-6 lg:hidden">
            <ReservationsCardList
              reservations={reservations}
              assets={assets}
              tags={tags}
              loading={isLoading}
              onEdit={handleEditReservation}
              onCancel={(reservationId) => setCancelTargetId(reservationId)}
              onPreview={handlePreviewReservation}
              className="md:grid-cols-2"
            />
          </div>
        </div>

        <Modal open={previewReservation !== null} onClose={closePreview}>
          <div className="grid gap-4">
            <div className="flex items-start justify-between border-b border-neutral-200 pb-4">
              <h2 className="text-base font-bold text-neutral-900">
                {sendAllMessageStrings.reservations.actions.preview}
              </h2>
              <button
                type="button"
                className="rounded-lg p-1 text-neutral-500 hover:bg-neutral-100"
                aria-label={commonStrings.close}
                onClick={closePreview}
              >
                <Icon icon="close" width={20} height={20} />
              </button>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-[320px] h-[min(600px,70dvh)] rounded-[3rem] border-8 border-neutral-50 flex flex-col overflow-hidden">
                <MessagePreview
                  sections={convertDraftSectionsToMessageSections(
                    previewReservation?.sections ?? [],
                  )}
                  assets={assets}
                  enableNameInsertionPreview
                />
              </div>
            </div>
          </div>
        </Modal>

        <ConfirmModal
          textTitle={sendAllMessageStrings.reservations.cancelConfirm.title}
          textMessage={sendAllMessageStrings.reservations.cancelConfirm.message}
          textCancel={strings.common.cancel}
          textConfirm={sendAllMessageStrings.reservations.actions.cancel}
          onClose={() => setCancelTargetId(null)}
          onConfirm={handleCancelReservation}
          open={!!cancelTargetId}
          isLoading={isCanceling}
        />
      </section>
    </>
  );
}

export default ReservationsList;
